import { useEffect, useState } from 'react';
import axios from 'axios';
import { Banknote, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AdminMmfSeedButton — P158·9 (Feb 2026).
 *
 * One-shot admin utility that seeds the 18 canonical Money Market Fund
 * tickers (SPAXX / FGXXX / VMFXX / SWVXX / TMCXX / etc.) into
 * `stock_cache` with `is_money_market_fund=True`. Self-hides for
 * non-admins AND self-hides once seeding is complete on THIS
 * environment (preview or production) — the button re-checks
 * `/admin/mmf-seed/status` after a successful click and drops out
 * of the DOM when `missing === 0`, so users don't see it after the
 * one-time setup is done.
 *
 * Placement: Dashboard admin-button row (mirrors AdminCommunityButton
 * pattern).
 */
export default function AdminMmfSeedButton() {
  const [isAdmin, setIsAdmin] = useState(false);
  const [needsSeed, setNeedsSeed] = useState(false);
  const [missing, setMissing] = useState(0);
  const [busy, setBusy] = useState(false);

  // Fetch role + seed status in one pass.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await axios.get(`${API}/auth/me`, { withCredentials: true });
        if (cancelled) return;
        if (me.data?.role !== 'admin') { setIsAdmin(false); return; }
        setIsAdmin(true);
        const s = await axios.get(`${API}/admin/mmf-seed/status`, { withCredentials: true });
        if (cancelled) return;
        setNeedsSeed(!!s.data?.needs_seed);
        setMissing(Number(s.data?.missing || 0));
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleClick = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/mmf-seed`, {}, { withCredentials: true });
      const { seeded_new = 0, stamped_existing = 0, total_mmf_tickers = 0 } = r.data || {};
      toast.success(
        `MMF seed complete — ${seeded_new} inserted, ${stamped_existing} flagged (${total_mmf_tickers} total)`,
        { duration: 5000 }
      );
      // Re-check status so we can auto-hide on success.
      const s = await axios.get(`${API}/admin/mmf-seed/status`, { withCredentials: true });
      setNeedsSeed(!!s.data?.needs_seed);
      setMissing(Number(s.data?.missing || 0));
    } catch (e) {
      toast.error(`MMF seed failed: ${e?.response?.data?.detail || e?.message || 'unknown error'}`);
    } finally {
      setBusy(false);
    }
  };

  // Self-hide: not admin OR seeding is already complete on this env.
  if (!isAdmin) return null;
  if (!needsSeed) return null;

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={busy}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-cyan-50 dark:bg-cyan-950/40 text-cyan-700 dark:text-cyan-300 border border-cyan-200 dark:border-cyan-800 text-xs font-semibold hover:bg-cyan-100 dark:hover:bg-cyan-900/40 disabled:opacity-60 disabled:cursor-not-allowed transition"
      data-testid="admin-mmf-seed-btn"
      title={`Seed the 18 canonical Money Market Fund tickers (SPAXX / FGXXX / VMFXX / SWVXX / …). One-time setup for this environment — button will disappear once complete. ${missing} still need seeding.`}
    >
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Banknote className="w-3.5 h-3.5" />}
      Seed MMF Tickers
      <span className="ml-1 px-1.5 py-0.5 rounded-full bg-cyan-500 text-white text-[10px] font-bold tabular-nums" data-testid="admin-mmf-seed-missing-count">
        {missing}
      </span>
    </button>
  );
}
